import React, { useEffect } from 'react';

import { useForm } from 'react-hook-form';

import { foodScoreStore } from '../../../../../stores/foodScoreStore';
import { useModalToggle } from '../../../../../../shared/hooks/useModalToggle';
import { openConfirmationModal } from '../../../../../../shared/features/confirmation';
import {
  FoodScorePayload,
  FoodScoreTypeName,
  FoodWithScore,
} from '../../../../../interfaces/FoodScorePageResponse';
import { ScoreFormData } from '../../ScoreForm/ScoreForm';

type UseFoodInfoRowProps = {
  food: FoodWithScore;
  toggleIsOpenCancelModal: () => void;
};

export const useFoodInfoRow = ({
  food,
  toggleIsOpenCancelModal,
}: UseFoodInfoRowProps) => {
  const { foodId } = food;
  const { saveFoodScore, deleteFood } = foodScoreStore;
  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitSuccessful },
    reset,
  } = useForm<ScoreFormData>();

  const [isScoreFormOpen, toggleScoreForm] = useModalToggle();

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [isSubmitSuccessful]);

  const getScoreResult = (formData: ScoreFormData): FoodScorePayload[] => {
    const result: FoodScorePayload[] = [];

    let key: FoodScoreTypeName;
    for (key in formData) {
      const value = formData[key];
      if (value !== null) {
        result.push({ foodScoreType: key, foodScoreValue: value });
      }
    }

    return result;
  };

  const onSave = async (data: ScoreFormData) => {
    const scoreResult = getScoreResult(data);

    await saveFoodScore(foodId, scoreResult);

    toggleScoreForm();
  };

  const submitForm = handleSubmit(onSave);

  const onDelete = (event: React.MouseEvent) => {
    event.stopPropagation();

    openConfirmationModal({
      title: 'The record will be deleted. Would you like to proceed?',
      confirmBtnConfig: { onClick: () => deleteFood(foodId) },
    });
  };

  const cancelModalYesHandler = () => {
    submitForm();
    toggleIsOpenCancelModal();
  };

  const cancelModalNoHandler = () => {
    cancelScoring();
    toggleIsOpenCancelModal();
  };

  const cancelScoring = () => {
    toggleScoreForm();
    reset();
  };

  const cancelHandler = () => {
    if (isDirty) {
      toggleIsOpenCancelModal();
    } else {
      toggleScoreForm();
    }
  };

  return {
    control,
    submitForm,
    onDelete,
    cancelHandler,
    cancelModalNoHandler,
    cancelModalYesHandler,
    isScoreFormOpen,
  };
};
